/**
 * Мост между выдачей US-18 и примитивами экрана `Stat*`
 * (задача бэка DP-427, контракт —
 * `digital-passport-v2/docs/US-18_agriculture_api.md`).
 *
 * Зачем отдельный слой, а не разбор сразу в `Stat*`:
 *  - **подписи приходят парой ru/kz**, и выбор языка обязан происходить на
 *    рендере: язык переключается без перезапроса данных;
 *  - **единица объявлена у каждой строки**, а не у блока (FR-3.2) — она
 *    переезжает в `StatValue.unit` строки, а не в заголовок;
 *  - **период приходит реквизитами**, а не готовой строкой: у периодных
 *    операций это год + подпись месяцев, у операций «на дату» — ISO-дата,
 *    у годовых — год урожая или уборки, и каждая подписывается по-своему;
 *  - **`no_data` и ноль — разные вещи** (FR-4.1), и разницу сохраняет
 *    состояние `StatValue`.
 *
 * Чего этот слой не делает намеренно: не пересчитывает единицы (объём уже
 * в млрд тенге), не собирает подпись базы ИФО (она готовая — `base_ru`),
 * не складывает строки в итоги и не выравнивает годы двух годовых блоков.
 */
import { useTranslation } from 'react-i18next';

import { stateDateLabel } from '../../../lib/report-date';
import type {
  AgricultureIfo,
  AgriculturePeriod,
  AgricultureMeasure,
  AgricultureRelease,
  StatIfo,
  StatPeriod,
  StatSource,
  StatValue,
} from '../../../types/analytics';

/** Ряд значения (US-18 FR-9.2) → ключ подписи. */
const SERIES_KEYS: Record<string, string> = {
  OPERATIONAL_MONTHLY: 'operational',
  ANNUAL_REFINED: 'annual',
};

export interface AgricultureView {
  isKz: boolean;
  /** Подпись ru/kz; пустая казахская → русская (US-18 «Локализация»). */
  label: (ru: string | null, kz: string | null) => string | null;
  /** Величина ответа → величина экрана со своей единицей и состоянием. */
  measure: (measure: AgricultureMeasure | null | undefined) => StatValue | null;
  ifo: (ifo: AgricultureIfo | null | undefined) => StatIfo | null;
  /** Период накопления подписью плашки: «2026 (январь–июль)». */
  period: (period: AgriculturePeriod | null | undefined) => StatPeriod | null;
  /**
   * Тот же период строкой внутри подписи блока: «январь–июль 2026».
   * Формы две, потому что на макете их две: у плашки период стоит
   * отдельной подписью, у блока — продолжением его названия.
   */
  periodInline: (period: AgriculturePeriod | null | undefined) => string | null;
  /** Дата состояния: «на 1 августа 2026 года» (US-18 FR-2.2). */
  stateDate: (iso: string | null | undefined) => StatPeriod | null;
  /** Годовой итог: подпись задаёт блок — год посева или год уборки. */
  year: (year: number | null | undefined, label: string) => StatPeriod | null;
  release: (release: AgricultureRelease | null | undefined) => StatSource | null;
  /** Охват совокупности строки (FR-3.5) — подписью блока. */
  coverage: (measure: AgricultureMeasure | null | undefined) => string | null;
  series: (series: string | null | undefined) => string | null;
}

export const useAgricultureView = (): AgricultureView => {
  const { t, i18n } = useTranslation();
  const isKz = i18n.language === 'kz';

  const label = (ru: string | null, kz: string | null): string | null =>
    (isKz ? (kz ?? ru) : ru) ?? null;

  /**
   * Год и подпись месяцев приходят РАЗНЫМИ полями (`year` + `label_ru`),
   * готовой строки у контракта нет — она собирается шаблоном локализации,
   * а не конкатенацией в вёрстке: порядок слов в казахском другой.
   */
  const periodText = (
    period: AgriculturePeriod | null | undefined,
    key: 'periodLabel' | 'periodInline',
  ): string | null => {
    if (period == null) return null;
    const months = label(period.label_ru, period.label_kz);
    if (period.year === null) return months;
    if (months === null) return String(period.year);
    return t(`analytics.agriculture.${key}`, {
      year: period.year,
      label: months,
    });
  };

  return {
    isKz,
    label,

    measure: (measure) => {
      if (measure == null) return null;
      return {
        value: measure.value,
        // Состояний у US-18 два — значение и «нет данных»: остальные
        // («х», «…», «-») источник агро не публикует, и придумывать их
        // отображению нельзя.
        state: measure.no_data ? 'no_data' : 'value',
        unit: label(measure.unit_ru, measure.unit_kz),
        calculated: false,
        formula: null,
        source: null,
      };
    },

    ifo: (ifo) => {
      if (ifo == null) return null;
      return {
        value: ifo.value,
        state: ifo.no_data ? 'no_data' : 'value',
        // Готовая подпись базы сравнения — «январь–июль 2025 года»
        // (FR-2.5); экран её не собирает вычитанием года.
        base_label: label(ifo.base_ru, ifo.base_kz),
        // Направление динамики контракт US-18 не отдаёт: для валового
        // выпуска рост — это всегда хорошо, и выдумывать признак
        // «сверху вниз» здесь нечего.
        direction: null,
        source: null,
      };
    },

    periodInline: (period) => periodText(period, 'periodInline'),

    period: (period) => {
      if (period == null) return null;
      const text = periodText(period, 'periodLabel');
      if (text === null) return null;
      return {
        type: 'cumulative',
        label: text,
        // Подмену спрошенного периода последним доступным (FR-2.3) экран
        // называет прямо — иначе цифра чужого периода выглядит своей.
        lagging: period.fallback_from_requested,
      };
    },

    stateDate: (iso) => {
      const text = stateDateLabel(iso, isKz);
      return text === null ? null : { type: 'date', label: text, lagging: false };
    },

    year: (year, text) =>
      year == null ? null : { type: 'year', label: text, lagging: false },

    release: (release) => {
      if (release == null) return null;
      const source: StatSource = {
        name: release.source_file,
        issue: release.source_sheet,
        published_at: release.published_at,
      };
      return source.name === null &&
        source.issue === null &&
        source.published_at === null
        ? null
        : source;
    },

    coverage: (measure) =>
      measure == null ? null : label(measure.coverage_ru, measure.coverage_kz),

    series: (series) => {
      const key = series == null ? undefined : SERIES_KEYS[series];
      return key === undefined
        ? null
        : t(`analytics.agriculture.series.${key}`);
    },
  };
};
